import {
  Typography,
  Descriptions,
  Tag,
  Divider,
  Progress,
  Alert,
  Space,
  Row,
  Col,
  Popover,
} from "antd";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { calculation } from "../data/calculationData";
import {
  calculateRepairRecommendation,
  RecommendationType,
} from "../lib/calculateRecommendationScore";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Modal } from "@/components/v2";

const { Title, Text, Paragraph } = Typography;

const recommendationLabelMap: Record<RecommendationType, string> = {
  repair: "Advies: herstellen",
  unsure: "Advies: nader beoordelen",
  totaled: "Advies: (economisch) total loss",
};

const adviesTagColorMap: Record<
  RecommendationType,
  "green" | "orange" | "red"
> = {
  repair: "green",
  unsure: "orange",
  totaled: "red",
};

const getStrokeColor = (score: number) => {
  if (score >= 70) return "#52c41a";
  if (score >= 40) return "#faad14";
  return "#ff4d4f";
};

interface ClaimDossierRecommendationModalProps {
  open: boolean;
  onClose: () => void;
  dossier?: Dossier;
}

export const ClaimDossierRecommendationModal = ({
  open,
  onClose,
  dossier,
}: ClaimDossierRecommendationModalProps) => {
  if (!dossier) return null;

  const kilometers = dossier.kilometers || 0;
  const buildYear = dossier.vehicle?.buildYear || new Date().getFullYear();
  const listPrice = dossier.vehicle?.listPrice || 0;
  const calculationTotal = calculation.totals.inclVat;

  const {
    estimatedVehicleValue,
    repairScore,
    recommendationType,
    ageYears,
    expectedKm,
    kmAdjustment,
    baseValue,
    baseScore,
    agePenalty,
    kmPenalty,
  } = calculateRepairRecommendation({
    kilometers,
    buildYear,
    calculationTotal,
    listPrice,
  });

  const costRatio = calculationTotal / estimatedVehicleValue;
  const costRatioPct = costRatio * 100;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      width={900}
      title="Volledige onderbouwing hersteladvies"
      styles={{ body: { padding: 24 } }}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <div>
          <Row justify="space-between" align="middle">
            <Col span={18}>
              <Title level={4} style={{ marginBottom: 4 }}>
                Samenvatting van het advies
              </Title>
              <Tag color={adviesTagColorMap[recommendationType]}>
                {recommendationLabelMap[recommendationType]}
              </Tag>
              <Paragraph type="secondary" style={{ marginTop: 8 }}>
                Dit advies is automatisch bepaald op basis van de ontvangen
                calculatie, de geschatte huidige voertuigwaarde en de leeftijd /
                kilometrage van het voertuig.
              </Paragraph>
            </Col>
            <Col span={6} style={{ textAlign: "center" }}>
              <Progress
                type="circle"
                percent={repairScore}
                strokeColor={getStrokeColor(repairScore)}
                size={90}
              />
            </Col>
          </Row>
        </div>

        <Divider className="my-0" />

        <div>
          <Title level={5}>Kosten versus voertuigwaarde</Title>
          <Descriptions
            colon={false}
            column={3}
            size="small"
            layout="horizontal"
          >
            <Descriptions.Item label="Bouwjaar">
              {buildYear} ({ageYears} jaar oud)
            </Descriptions.Item>

            <Descriptions.Item
              label={
                <Space size={4}>
                  Basiswaarde (afschrijving)
                  <Popover
                    title="Afschrijvingsformule"
                    trigger="hover"
                    content={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">
                          De basiswaarde wordt berekend op basis van een{" "}
                          <strong>degressief afschrijvingsmodel</strong>.
                        </Text>

                        <Text strong>Fase 1: 0–3 jaar</Text>
                        <Text type="secondary">
                          Jaarlijkse waardedaling van ca. 22%:
                        </Text>
                        <code>
                          waarde = listPrice × 0.78<sup>jaren</sup>
                        </code>

                        <Text strong>Fase 2: 4–7 jaar</Text>
                        <Text type="secondary">
                          Daarna een gematigde afschrijving van 16% per jaar:
                        </Text>
                        <code>
                          waarde = listPrice × 0.78<sup>3</sup> × 0.84
                          <sup>(jaren − 3)</sup>
                        </code>

                        <Text strong>Fase 3: 8+ jaar</Text>
                        <Text type="secondary">
                          Vanaf 8 jaar wordt een sterkere waardedaling toegepast
                          van 30% per jaar:
                        </Text>
                        <code>
                          waarde = listPrice × 0.78<sup>3</sup> × 0.84
                          <sup>4</sup> × 0.70<sup>(jaren − 7)</sup>
                        </code>
                      </Space>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Popover>
                </Space>
              }
            >
              € {baseValue.toFixed(2).replace(".", ",")}
            </Descriptions.Item>

            <Descriptions.Item label="Geschatte voertuigwaarde">
              € {estimatedVehicleValue.toFixed(2).replace(".", ",")}
            </Descriptions.Item>

            <Descriptions.Item label="Kilometrage">
              {kilometers.toLocaleString("nl-NL")} km
            </Descriptions.Item>

            <Descriptions.Item
              label={
                <Space size={4}>
                  Verwachte km
                  <Popover
                    title="Verwachte kilometrage"
                    trigger="hover"
                    content={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">
                          Verwachte kilometrage op basis van leeftijd:
                        </Text>
                        <code>
                          verwachteKm = max(1, voertuigLeeftijdInJaren) × 25.000
                        </code>
                        <Text type="secondary">
                          Er wordt gerekend met ~25.000 km per jaar als normaal
                          gebruik.
                        </Text>
                      </Space>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Popover>
                </Space>
              }
            >
              {expectedKm.toLocaleString("nl-NL")} km
            </Descriptions.Item>

            <Descriptions.Item label="Ontvangen calculatie">
              € {calculationTotal.toFixed(2).replace(".", ",")}
            </Descriptions.Item>

            <Descriptions.Item label="Cataloguswaarde">
              € {listPrice.toFixed(2).replace(".", ",")}
            </Descriptions.Item>

            <Descriptions.Item
              label={
                <Space size={4}>
                  Km-correctiefactor
                  <Popover
                    title="Kilometercorrectie"
                    trigger="hover"
                    content={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">
                          Correctie op de basiswaarde o.b.v. afwijking t.o.v.
                          expectedKm:
                        </Text>
                        <code>kmDelta = kilometers − verwachteKm</code>
                        <code>
                          ruweKmCorrectie = 1 − (kmDelta / 50.000) × 0.10
                        </code>
                        <code>
                          kmCorrectie = clamp(0.7, 1.1, ruweKmCorrectie)
                        </code>
                        <Text type="secondary">
                          Elke 50.000 km extra ≈ 10% waardedaling, met een
                          minimum van 0,7 en maximum van 1,1.
                        </Text>
                      </Space>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Popover>
                </Space>
              }
            >
              {kmAdjustment.toFixed(2)}
            </Descriptions.Item>

            <Descriptions.Item
              label={
                <Space size={4}>
                  Kosten / waarde
                  <Popover
                    title="Kosten/waarde-ratio"
                    trigger="hover"
                    content={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">
                          Verhouding tussen schadebedrag en geschatte
                          voertuigwaarde:
                        </Text>
                        <code>
                          kostenRatio = calculatieBedrag /
                          geschatteVoertuigWaarde
                        </code>
                        <Text type="secondary">
                          Deze ratio (in %) vormt de basis voor de herstelscore:
                          hoe hoger het percentage, hoe minder aantrekkelijk
                          herstel.
                        </Text>
                      </Space>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Popover>
                </Space>
              }
            >
              {costRatioPct.toFixed(1)}%
            </Descriptions.Item>
          </Descriptions>

          <Alert
            className="mt-8"
            showIcon
            type={costRatioPct <= 35 ? "success" : "warning"}
            message={
              costRatioPct <= 35
                ? "De reparatiekosten blijven binnen een economisch verdedigbare bandbreedte."
                : "De reparatiekosten liggen relatief hoog ten opzichte van de voertuigwaarde."
            }
            description={
              <>
                De calculatie bedraagt ongeveer{" "}
                <strong>{costRatioPct.toFixed(1).replace(".", ",")}%</strong>{" "}
                van de geschatte voertuigwaarde. Hoe hoger dit percentage, hoe
                minder aantrekkelijk herstel wordt ten opzichte van (economisch)
                total loss.
              </>
            }
          />
        </div>

        <Divider className="my-0" />

        <div>
          <Title level={5}>Opbouw van de herstelscore</Title>
          <Descriptions column={2} size="small">
            <Descriptions.Item
              label={
                <Space size={4}>
                  Basis-score (schade vs waarde)
                  <Popover
                    title="Basis-score"
                    trigger="hover"
                    content={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">
                          De basis-score kijkt alleen naar de verhouding tussen
                          schadebedrag en geschatte voertuigwaarde:
                        </Text>
                        <code>basisScore = (1 − kostenRatio) × 100</code>
                        <Text type="secondary">
                          waarbij{" "}
                          <code>
                            kostenRatio = calculatieBedrag /
                            geschatteVoertuigWaarde
                          </code>
                          , begrensd tussen 0 en 100.
                        </Text>
                      </Space>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Popover>
                </Space>
              }
            >
              {baseScore.toFixed(1).replace(".", ",")} / 100
            </Descriptions.Item>

            <Descriptions.Item
              label={
                <Space size={4}>
                  Leeftijds-penalty
                  <Popover
                    title="Leeftijds-correctie"
                    trigger="hover"
                    content={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">
                          Oudere voertuigen krijgen een aftrek op de
                          basis-score:
                        </Text>
                        <code>
                          leeftijdsPenalty = max(0, voertuigLeeftijdInJaren − 5)
                          × 4
                        </code>
                        <Text type="secondary">
                          Pas vanaf 5 jaar wordt er 4 punten per extra jaar
                          afgetrokken.
                        </Text>
                      </Space>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Popover>
                </Space>
              }
            >
              −{agePenalty.toFixed(1).replace(".", ",")} punten
            </Descriptions.Item>

            <Descriptions.Item
              label={
                <Space size={4}>
                  Kilometrage-penalty
                  <Popover
                    title="Kilometrage-correctie"
                    trigger="hover"
                    content={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">
                          Zeer hoge kilometrage verlaagt de score extra:
                        </Text>
                        <code>
                          kmPenalty = max(0, (kilometers − 140.000) / 30.000) ×
                          4
                        </code>
                        <Text type="secondary">
                          Elke 30.000 km boven 140.000 km ≈ −4 punten op de
                          score.
                        </Text>
                      </Space>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Popover>
                </Space>
              }
            >
              −{kmPenalty.toFixed(1).replace(".", ",")} punten
            </Descriptions.Item>

            <Descriptions.Item
              label={
                <Space size={4}>
                  Eindscore (herstel-score)
                  <Popover
                    title="Eindscore"
                    trigger="hover"
                    content={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">
                          De herstelscore is de basis-score minus de correcties:
                        </Text>
                        <code>
                          repairScore = clamp(0, 100, baseScore − agePenalty −
                          kmPenalty)
                        </code>
                        <Text type="secondary">
                          De score wordt begrensd tussen 0 en 100 en bepaalt het
                          advies (&quot;herstellen&quot;, &quot;nader
                          beoordelen&quot; of &quot;total loss&quot;).
                        </Text>
                      </Space>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Popover>
                </Space>
              }
            >
              {repairScore.toFixed(1).replace(".", ",")} / 100
            </Descriptions.Item>
          </Descriptions>

          <Paragraph type="secondary" style={{ marginTop: 8 }}>
            Eerst wordt een basis-score berekend op basis van de verhouding
            tussen schadebedrag en geschatte voertuigwaarde. Daarna wordt deze
            score verlaagd voor oudere voertuigen en voertuigen met een zeer
            hoge kilometrage. De uiteindelijke herstelscore bepaalt het advies
            (&quot;herstellen&quot;, &quot;nader beoordelen&quot; of &quot;total
            loss&quot;).
          </Paragraph>
        </div>
      </Space>
    </Modal>
  );
};
